import { cloneDeep, isEqual } from 'lodash'
import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useConsumerCart } from 'shop/hooks'
import { strippedContactNumPrefix } from '../Inputs/utils'
import { UpdateDateCartRecipientDetails } from './Network'
import { CheckoutForm, FormFields, FORM_DEFAULTS } from './types'

type FormFieldCartUpdateArgs = {
  formRef: React.RefObject<HTMLFormElement | HTMLDivElement>
  checkoutForm: UseFormReturn<CheckoutForm, any, CheckoutForm>
  event: FocusEvent
  prevFieldValue: React.MutableRefObject<any | undefined>
  formFieldName: keyof FormFields
  updateConsumerCart: ReturnType<typeof useConsumerCart>['updateConsumerCart']
  handleCloseSection?: () => void
}

export const handleFormFieldCartUpdate = async ({
  formRef,
  checkoutForm,
  event,
  prevFieldValue,
  formFieldName,
  updateConsumerCart,
  handleCloseSection
}: FormFieldCartUpdateArgs) => {
  const relatedTarget = event.relatedTarget as HTMLElement | null

  // If blur happens and focus doesn't move to another element inside the form section
  const isLeavingForm =
    !relatedTarget || !formRef.current?.contains(relatedTarget)
  if (isLeavingForm) {
    const fieldValue = checkoutForm.getValues(formFieldName)
    const valuesSameAsPrev = isEqual(fieldValue, prevFieldValue.current)
    const valuesAreDefault = isFormFieldDefaultValue(formFieldName, fieldValue)

    if (valuesAreDefault && !valuesSameAsPrev) {
      // User has deleted all values in the input section so reset it on the cart
      updateConsumerCart({ [formFieldName]: null })
      // reset prev ref
      prevFieldValue.current = cloneDeep(fieldValue)
      return
    }
    // Input section has values, check if they are valid
    const isValid = await checkoutForm.trigger(formFieldName)
    if (!isValid) return

    if (handleCloseSection) handleCloseSection()

    if (valuesSameAsPrev) return

    // update cart with new values
    const valuesToUpdate = mapFieldToCartArg(formFieldName, fieldValue)
    if (!valuesToUpdate) return
    // update the prev with new value
    prevFieldValue.current = cloneDeep(fieldValue)

    updateConsumerCart(valuesToUpdate)
  }
}

const mapFieldToCartArg = <T extends keyof FormFields>(
  formFieldName: T,
  formfieldValue: FormFields[T]
) => {
  switch (formFieldName) {
    case 'customerDetails': {
      const { contactNumPrefix, contactNumber, ...otherCustomerDetails } =
        formfieldValue as FormFields['customerDetails']
      const customerDetails = {
        ...otherCustomerDetails,
        contactNumber: `${strippedContactNumPrefix(
          contactNumPrefix
        )}${contactNumber}`
      }
      return { customerDetails }
    }
    case 'recipientDetails': {
      const recipientDetails = formfieldValue as FormFields['recipientDetails']
      let amendedRecipientDetails = amendRecipientDetails(recipientDetails)
      if (amendedRecipientDetails) {
        return { recipientDetails: amendedRecipientDetails }
      }
      return null
    }
    case 'orderNotes': {
      const orderNotes = formfieldValue as FormFields['orderNotes']
      return { orderNotes }
    }
    case 'customFieldValue': {
      const customFieldValue = formfieldValue as FormFields['customFieldValue']
      return { customFieldValue }
    }
    case 'dropoffNotes': {
      const dropoffNotes = formfieldValue as FormFields['dropoffNotes']
      return { dropoffNotes }
    }
    default:
      return null
  }
}

export const amendRecipientDetails = (
  recipientDetails: FormFields['recipientDetails']
): UpdateDateCartRecipientDetails | null => {
  let amendedRecipientDetails: UpdateDateCartRecipientDetails = recipientDetails
  if (recipientDetails?.firstName) {
    const {
      contactNumPrefix: rec_prefix,
      contactNumber: rec_num,
      ...otherRecipientDetails
    } = recipientDetails

    amendedRecipientDetails = {
      ...otherRecipientDetails,
      contactNumber: `${strippedContactNumPrefix(rec_prefix)}${rec_num}`
    }
    return amendedRecipientDetails
  }
  return null
}

const isFormFieldDefaultValue = <T extends keyof FormFields>(
  formField: T,
  formFieldValue: FormFields[T]
) => {
  if (isEqual(formFieldValue, FORM_DEFAULTS[formField])) return true
  return false
}
